import { createCrudStore } from "./crudStore";
import { apiService } from "@/services/api";

const empresasStore = createCrudStore("empresas", "empresas");

export default {
  ...empresasStore,

  state: {
    ...empresasStore.state,
    cobros: [], // Lista de cobros del mes actual
    cobrosLoading: false,
  },

  getters: {
    ...empresasStore.getters,
    cobros(state) {
      return state.cobros;
    },
    cobrosLoading(state) {
      return state.cobrosLoading;
    },
  },

  mutations: {
    ...empresasStore.mutations,
    setCobros(state, cobros) {
      state.cobros = cobros;
    },
    addCobro(state, cobro) {
      state.cobros.push(cobro);
    },
    updateCobro(state, updatedCobro) {
      const index = state.cobros.findIndex((c) => c.id === updatedCobro.id);
      if (index !== -1) {
        state.cobros.splice(index, 1, updatedCobro);
      }
    },
    removeCobro(state, cobroId) {
      state.cobros = state.cobros.filter((c) => c.id !== cobroId);
    },
    setCobrosLoading(state, value) {
      state.cobrosLoading = value;
    },
  },

  actions: {
    ...empresasStore.actions,
    getEmpresas: empresasStore.actions.getAllStates,
    postEmpresa: empresasStore.actions.create,
    patchEmpresa: empresasStore.actions.update,
    deleteEmpresa: empresasStore.actions.delete,

    /**
     * Registrar un cobro para una empresa
     * POST /empresas/{empresa_id}/cobros
     */
    async postCobro({ dispatch, commit, state }, { empresaId, cobroData }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => {
          return apiService.postCustom(
            `/empresas/${empresaId}/cobros`,
            cobroData
          );
        },
        onSuccessCallback: async (commitFn, response) => {
          // Buscar el nombre de la empresa
          const empresa = state.empresas.find((e) => e.id === empresaId);
          const cobroConEmpresa = {
            ...response.data,
            empresa_nombre: empresa?.empresa || "",
          };
          // Agregar el cobro al estado con el nombre de empresa
          commit("addCobro", cobroConEmpresa);
        },
        errorMessage: "Error al registrar el cobro",
      });
    },

    /**
     * Obtener cobros de una empresa
     * GET /empresas/{empresa_id}/cobros
     */
    async getCobrosEmpresa({ dispatch }, { empresaId, desde, hasta }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => {
          let url = `/empresas/${empresaId}/cobros`;
          const params = [];
          if (desde) params.push(`desde=${desde}`);
          if (hasta) params.push(`hasta=${hasta}`);
          if (params.length > 0) url += `?${params.join("&")}`;
          return apiService.getCustom(url);
        },
        onSuccessCallback: (commitFn, response) => {
          // Retornar los cobros sin modificar el estado
        },
        errorMessage: "Error al obtener los cobros",
      });
    },

    /**
     * Obtener todos los cobros de todas las empresas para un período
     * Itera sobre las empresas y obtiene sus cobros
     */
    async getCobrosMes({ commit, state, dispatch }, { desde, hasta }) {
      commit("setCobrosLoading", true);
      try {
        const empresas = state.empresas;
        const todosLosCobros = [];

        for (const empresa of empresas) {
          try {
            const response = await apiService.getCustom(
              `empresas/${empresa.id}/cobros?desde=${desde}&hasta=${hasta}`
            );
            if (response.data && response.data.length > 0) {
              // Agregar nombre de empresa a cada cobro
              const cobrosConEmpresa = response.data.map((cobro) => ({
                ...cobro,
                empresa_nombre: empresa.empresa,
              }));
              todosLosCobros.push(...cobrosConEmpresa);
            }
          } catch (e) {
            // Continuar con la siguiente empresa si hay error
            console.warn(
              `Error obteniendo cobros de empresa ${empresa.id}:`,
              e
            );
          }
        }

        commit("setCobros", todosLosCobros);
        return todosLosCobros;
      } catch (error) {
        console.error("Error obteniendo cobros del mes:", error);
        throw error;
      } finally {
        commit("setCobrosLoading", false);
      }
    },

    /**
     * Actualizar un cobro existente
     * PATCH /empresas/cobros/{cobro_id}
     */
    async patchCobro({ dispatch, commit, state }, { cobroId, cobroData }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => {
          return apiService.patchCustom(
            `/empresas/cobros/${cobroId}`,
            cobroData
          );
        },
        onSuccessCallback: async (commitFn, response) => {
          // Buscar el cobro original para mantener empresa_nombre
          const cobroOriginal = state.cobros.find((c) => c.id === cobroId);
          const cobroActualizado = {
            ...response.data,
            empresa_nombre: cobroOriginal?.empresa_nombre || "",
          };
          commit("updateCobro", cobroActualizado);
        },
        errorMessage: "Error al actualizar el cobro",
      });
    },

    /**
     * Eliminar un cobro
     * DELETE /empresas/cobros/{cobro_id}
     */
    async deleteCobro({ dispatch, commit }, cobroId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => {
          return apiService.deleteCustom(`empresas/cobros/${cobroId}`);
        },
        onSuccessCallback: async (commitFn, response) => {
          commit("removeCobro", cobroId);
        },
        errorMessage: "Error al eliminar el cobro",
      });
    },

    /**
     * Obtener comparación de cobros vs esperado para una empresa
     * GET /empresas/{empresa_id}/comparacion-cobros
     */
    async getComparacionCobros({ dispatch }, { empresaId, desde, hasta }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => {
          return apiService.getCustom(
            `/empresas/${empresaId}/comparacion-cobros?desde=${desde}&hasta=${hasta}`
          );
        },
        onSuccessCallback: (commitFn, response) => {
          // Retornar los datos sin modificar el estado
          return response.data;
        },
        errorMessage: "Error al obtener comparación de cobros",
      });
    },
  },
};
